import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import { apiGet, apiPost, errOf } from '../../lib/api';

interface SendView {
  id: string;
  subject: string;
  status: string;
  purpose: string;
}
interface Stats {
  total: number;
  pending: number;
  simulated: number;
  sent: number;
  failed: number;
  accepted: number;
  declined: number;
}
interface RecRow {
  id: string;
  email: string;
  name: string;
  subject: string;
  body: string;
  status: string;
  error: string;
  rsvp_status: string;
  rsvp_guests: number;
  provider_id: string | null;
}

const btn = { border: 'none', cursor: 'pointer' } as const;
const box = { border: '1px solid var(--color-divider)', background: 'var(--color-card)', color: 'var(--color-text)' } as const;

/** 寄送批次詳情 —— **預覽（＝實際寄出）** ／ 分批寄 ／ 模擬寄 ／ 狀態表 ／ RSVP 連結 ／ 匯出 CSV */
export default function AdminLetterSendPage() {
  const { t } = useTranslation();
  const { id = '' } = useParams();
  const [send, setSend] = useState<SendView | null>(null);
  const [stats, setStats] = useState<Stats | null>(null);
  const [rows, setRows] = useState<RecRow[]>([]);
  const [configured, setConfigured] = useState(true);
  const [openId, setOpenId] = useState('');
  const [link, setLink] = useState('');
  const [copied, setCopied] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');

  const load = useCallback(async () => {
    const r = await apiGet<{ send: SendView; stats: Stats; recipients: RecRow[]; emailConfigured: boolean }>(
      `/api/admin/letters/sends/${id}`,
    );
    if (!r.ok || !r.data) {
      setErr(errOf(r.data) || t('ltr_failed'));
      return;
    }
    setSend(r.data.send);
    setStats(r.data.stats);
    setRows(r.data.recipients);
    setConfigured(r.data.emailConfigured);
  }, [id, t]);

  useEffect(() => {
    void load();
  }, [load]);

  async function doSend(dryRun: boolean) {
    setBusy(true);
    setErr('');
    setMsg('');
    const r = await apiPost<{ processed: number; sent: number; failed: number; remaining: number }>(
      `/api/admin/letters/sends/${id}/send`,
      { limit: 10, dryRun },
    );
    setBusy(false);
    if (!r.ok || !r.data) {
      setErr(errOf(r.data) || t('ltr_failed'));
      return;
    }
    setMsg(t('ltr_send_result', { n: r.data.processed, remaining: r.data.remaining }));
    await load();
  }

  async function rotate(recId: string) {
    setErr('');
    const r = await apiPost<{ url: string }>('/api/admin/letters/rotate-rsvp', { letter_recipient_id: recId });
    if (!r.ok || !r.data) {
      setErr(errOf(r.data) || t('ltr_failed'));
      return;
    }
    setLink(r.data.url);
    setMsg(t('ltr_rotate_note'));
  }

  function exportCsv() {
    const head = ['email', 'name', 'status', 'rsvp_status', 'rsvp_guests', 'provider_id', 'error'];
    const esc = (v: unknown): string => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const body = rows
      .map((r) => [r.email, r.name, r.status, r.rsvp_status, r.rsvp_guests, r.provider_id ?? '', r.error].map(esc).join(','))
      .join('\n');
    const blob = new Blob([`${head.join(',')}\n${body}`], { type: 'text/csv;charset=utf-8' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `letter-send-${id.slice(0, 8)}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  }

  return (
    <div className="max-w-3xl">
      <Link to="/admin/letters" className="text-sm" style={{ color: 'var(--color-primary)' }}>
        ← {t('ltr_back')}
      </Link>

      {!send || !stats ? (
        <p className="text-sm mt-3" style={{ color: err ? 'var(--color-danger)' : 'var(--color-text-secondary)' }}>
          {err || '…'}
        </p>
      ) : (
        <>
          <h2 className="font-bold text-lg mt-2 mb-1">{send.subject}</h2>
          <p className="text-xs mb-3" style={{ color: 'var(--color-text-secondary)' }}>
            {t(`ltr_status_${send.status}`)}
            {` ｜ ${t('ltr_stat_pending')} ${stats.pending}`}
            {` ｜ ${t('ltr_stat_sent')} ${stats.sent}`}
            {` ｜ ${t('ltr_stat_simulated')} ${stats.simulated}`}
            {` ｜ ${t('ltr_stat_failed')} ${stats.failed}`}
            {` ｜ ${t('ltr_stat_accepted')} ${stats.accepted}`}
            {` ｜ ${t('ltr_stat_declined')} ${stats.declined}`}
          </p>

          {!configured && (
            <p className="text-sm mb-3 rounded-lg p-2" style={{ ...box, color: 'var(--color-danger)' }}>
              {t('ltr_email_unconfigured')}
            </p>
          )}

          <div className="flex flex-wrap gap-2 mb-3">
            <button
              type="button"
              disabled={busy || stats.pending === 0}
              onClick={() => void doSend(false)}
              className="rounded-lg px-4 py-1.5 text-sm font-semibold"
              style={{ ...btn, background: 'var(--color-primary)', color: 'var(--color-on-primary)', opacity: busy || stats.pending === 0 ? 0.6 : 1 }}
            >
              {t('ltr_send_now', { n: 10 })}
            </button>
            <button
              type="button"
              disabled={busy || stats.pending === 0}
              onClick={() => void doSend(true)}
              className="rounded-lg px-4 py-1.5 text-sm font-semibold"
              style={{ ...box, cursor: 'pointer', opacity: busy || stats.pending === 0 ? 0.6 : 1 }}
            >
              {t('ltr_send_dry')}
            </button>
            <button type="button" onClick={exportCsv} className="rounded-lg px-4 py-1.5 text-sm" style={{ ...box, cursor: 'pointer' }}>
              {t('ltr_export')}
            </button>
          </div>

          {msg && <p className="text-sm mb-2" style={{ color: 'var(--color-primary)' }}>{msg}</p>}
          {err && <p className="text-sm mb-2" style={{ color: 'var(--color-danger)' }}>{err}</p>}
          {link && (
            <p className="text-xs mb-3 break-all rounded-lg p-2" style={{ ...box }}>
              {link}
              <button
                type="button"
                className="ml-2 underline"
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-primary)' }}
                onClick={() => {
                  void navigator.clipboard?.writeText(link);
                  setCopied(link);
                }}
              >
                {copied === link ? t('ltr_copied') : t('ltr_copy')}
              </button>
            </p>
          )}

          <h3 className="font-bold mb-2">{t('ltr_recipients')}</h3>
          <ul className="text-sm">
            {rows.map((r) => (
              <li key={r.id} className="rounded-lg p-2 mb-1" style={box}>
                <div className="flex items-center justify-between gap-2">
                  <button type="button" className="text-left flex-1" style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text)' }} onClick={() => setOpenId(openId === r.id ? '' : r.id)}>
                    {r.email}
                    {r.name ? `（${r.name}）` : ''}{' '}
                    <span style={{ color: 'var(--color-text-secondary)', fontSize: 'var(--font-size-tiny)' }}>
                      ｜ {t(`ltr_status_${r.status}`)}
                      {r.rsvp_status !== 'none' ? ` ｜ ${t(`rsvp_${r.rsvp_status}`)}${r.rsvp_guests ? ` ×${r.rsvp_guests}` : ''}` : ''}
                    </span>
                  </button>
                  {r.status !== 'pending' && r.rsvp_status === 'none' && (
                    <button type="button" onClick={() => void rotate(r.id)} className="text-xs" style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-primary)' }}>
                      {t('ltr_rotate')}
                    </button>
                  )}
                </div>
                {openId === r.id && (
                  <div className="mt-2 text-xs" style={{ color: 'var(--color-text-secondary)' }}>
                    <div className="font-semibold mb-1">{r.subject}</div>
                    <pre className="whitespace-pre-wrap" style={{ fontFamily: 'inherit' }}>
                      {r.body}
                    </pre>
                    {r.error && <p style={{ color: 'var(--color-danger)' }}>{r.error}</p>}
                  </div>
                )}
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}
